import { apiClient } from "@/shared/apiClient";
import type { PatrolConfig, PatrolScanResult, PatrolStatus } from "./types";

function pickOptionalPeriodStartedAt(obj: Record<string, unknown>): string | undefined {
  for (const key of [
    "period_started_at",
    "current_period_started_at",
    "last_patrol_scanned_at",
    "last_patrol_at",
  ] as const) {
    const v = obj[key];
    if (v != null && String(v).trim() !== "") return String(v);
  }
  return undefined;
}

export const patrolApi = {
  async getPatrolConfig(): Promise<PatrolConfig | null> {
    const response = await apiClient.get("/api/employee/patrol/config");
    const data = response.data !== undefined ? response.data : response;
    if (data === null || data === undefined || typeof data !== "object") {
      return null;
    }
    const obj = data as Record<string, unknown>;
    if (obj.min_qr_codes_to_scan === undefined && obj.patrol_interval_minutes === undefined) {
      return null;
    }
    return {
      min_qr_codes_to_scan: Number(obj.min_qr_codes_to_scan) || 0,
      patrol_interval_minutes: Number(obj.patrol_interval_minutes) || 0,
    };
  },

  async getPatrolStatus(): Promise<PatrolStatus | null> {
    const response = await apiClient.get("/api/employee/patrol/status");
    const data = response.data !== undefined ? response.data : response;
    if (data === null || data === undefined || typeof data !== "object") {
      return null;
    }
    const obj = data as Record<string, unknown>;
    if (
      obj.min_qr_codes_to_scan === undefined &&
      obj.patrol_interval_minutes === undefined &&
      obj.unique_scanned_in_period === undefined
    ) {
      return null;
    }
    const period_started_at = pickOptionalPeriodStartedAt(obj);
    return {
      min_qr_codes_to_scan: Number(obj.min_qr_codes_to_scan) ?? 0,
      patrol_interval_minutes: Number(obj.patrol_interval_minutes) ?? 0,
      unique_scanned_in_period: Number(obj.unique_scanned_in_period) ?? 0,
      window_start: String(obj.window_start ?? ""),
      window_end: String(obj.window_end ?? ""),
      ...(period_started_at ? { period_started_at } : {}),
    };
  },

  async postPatrolScan(payload: string, shiftId?: number): Promise<PatrolScanResult> {
    const body: { payload: string; shift_id?: number } = { payload };
    if (shiftId != null) {
      body.shift_id = shiftId;
    }
    const response = await apiClient.post("/api/employee/patrol/scan", body);
    const raw = response.data !== undefined ? response.data : response;
    return {
      qr_code: raw.qr_code,
      scanned_at: raw.scanned_at,
    };
  },
};
